import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TablePagination,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Avatar,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Devices as DevicesIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  Battery20 as BatteryIcon,
  Speed as SpeedIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import axiosInstance from '../utlis/axiosInstance';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../utlis/useTheme';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import styles from './UserDevices.module.css';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.05)',
  backgroundColor: '#fafafa',
}));

// Interfaces
interface Device {
  id: number;
  name: string;
  unique_id: string;
  status: string;
  disabled: boolean;
  position_id: number;
  group: any;
  phone: string;
  model: string;
  contact: string;
  category: string;
  attributes: {
    port: {
      internalBattery?: boolean;
    };
    color: string;
    battery: string;
    mdn_number: string;
    customer_name: string;
    sat: number;
    odometer: number;
    ignition: boolean;
    charge: boolean;
    blocked: boolean;
    battery_level: string;
    distance: number;
    total_distance: number;
    today_distance: number;
    hours: number;
    idle_at: string | null;
    running_at: string | null;
    stopped_at: string | null;
    ignition_off_at: string | null;
    inactive_at: string | null;
    fuel: any;
    today_idle_time: number;
    total_running: number;
    total_idle: number;
    total_stopped: number;
    ignition_off_since: number;
    running_since: number;
    stopped_since: number;
    today_max_speed: number;
    device_online: boolean;
    ac: any;
    status: string;
    latest_speed: number;
    last_updated_at: number;
    inactive_since: number;
  };
  odometer: number;
  expiration_time: string;
  documents: any;
  device_type: string;
  speed_limit: any;
  inventory: boolean;
  ignition_on_alert: boolean;
  is_signed_up: boolean;
  sim_number: string;
  activated_on: string | null;
  latest_position: {
    id: number;
    gps_data: {
      alarm: any[];
      speed: number;
      address: any;
      gps_info: {
        "GPS Length": number;
        "Satellites": number;
      };
      latitude: number;
      date_time: string;
      longitude: number;
      packet_type: string;
      terminal_id: string;
      course_status: {
        "Course": number;
        "Status": number;
      };
      incoming_data: string;
      protocol_number: string;
    };
    protocol: string;
    created_at: number;
    updated_at: number;
  } | null;
  latest_heartbeat: {
    id: number;
    gps_data: {
      alarm: any[];
      timezone: number;
      packet_type: string;
      terminal_id: string;
      battery_power: number;
      incoming_data: string;
      protocol_number: string;
      signal_strength: number;
      upload_interval: number;
      software_version: number;
    };
    protocol: string;
    created_at: number;
    updated_at: number;
  } | null;
}

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  administrator: boolean;
  disabled: boolean;
}

const UserDevices: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const muiTheme = useMuiTheme();
  const { getPrimaryColor, getPrimaryLightColor, getPrimaryDarkColor } = useCustomTheme();

  // State management
  const [devices, setDevices] = useState<Device[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Helper functions
  const formatDate = (timestamp: number): string => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp * 1000);
      return format(date, 'dd:MM:yyyy');
    } catch (error) {
      return 'N/A';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'online':
        return '#10b981';
      case 'offline':
        return '#ef4444';
      case 'idle':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getBatteryColor = (level: string): string => {
    const batteryLevel = parseInt(level);
    if (batteryLevel > 50) return '#10b981';
    if (batteryLevel > 20) return '#f59e0b';
    return '#ef4444';
  };

  // API Functions
  const fetchUser = async () => {
    if (!userId) return;

    try {
      const response = await axiosInstance.get(`/gpstracker/userhandler/${userId}`);
      if (response.data && response.data.status && response.data.responseData) {
        setUser(response.data.responseData);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setErrorMessage('Error fetching user information.');
    }
  };

  const fetchUserDevices = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get('/gpstracker/devicehandler/', {
        params: {
          limit: 10000,
          offset: 0,
          user_id: parseInt(userId),
        },
      });

      // Handle the response format with status and responseData
      if (response.data && response.data.status && response.data.responseData) {
        const userDevices = response.data.responseData;
        setDevices(userDevices);
      } else if (response.data && Array.isArray(response.data)) {
        // Fallback for direct array response
        setDevices(response.data);
      } else {
        setDevices([]);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setErrorMessage('Error fetching user devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchUserDevices();
    setSuccessMessage('Devices refreshed successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Event handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Effects
  useEffect(() => {
    fetchUser();
    fetchUserDevices();
  }, [userId]);

  // Filter devices based on search
  const filteredDevices = devices.filter((device) =>
    device.name.toLowerCase().includes(search.toLowerCase()) ||
    device.unique_id.toLowerCase().includes(search.toLowerCase()) ||
    device.model.toLowerCase().includes(search.toLowerCase()) ||
    device.category.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedDevices = filteredDevices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!userId) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Alert severity="error">
          Invalid user ID provided.
        </Alert>
      </Box>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTop}>
            <div className={styles.userInfo}>
              <Avatar
                sx={{
                  bgcolor: user?.administrator ? getPrimaryColor() : '#6b7280',
                  width: 48,
                  height: 48,
                  mr: 2,
                }}
              >
                <PersonIcon />
              </Avatar>
              <div>
                <h1 className={styles.userName}>{user?.name || 'Loading...'}</h1>
                <p className={styles.userEmail}>{user?.email || ''}</p>
                {user && (
                  <div className={styles.userDetails}>
                    <Chip
                      label={user.administrator ? 'Administrator' : 'User'}
                      size="small"
                      sx={{
                        bgcolor: user.administrator ? alpha(getPrimaryColor(), 0.1) : '#f1f5f9',
                        color: user.administrator ? getPrimaryColor() : '#475569',
                        mr: 1,
                      }}
                    />
                    <Chip
                      label={user.disabled ? 'Disabled' : 'Active'}
                      size="small"
                      sx={{
                        bgcolor: user.disabled ? '#fee2e2' : '#dcfce7',
                        color: user.disabled ? '#991b1b' : '#166534',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className={styles.headerDescription}>
            Manage and monitor all devices assigned to this user
          </p>
        </div>
        <div className={styles.actionButtons}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              borderColor: getPrimaryColor(),
              color: getPrimaryColor(),
              '&:hover': {
                borderColor: getPrimaryLightColor(),
                bgcolor: alpha(getPrimaryColor(), 0.05),
              },
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* Search */}
      <div className={styles.searchSection}>
        <div className={styles.searchContainer}>
          <div className={styles.searchInputWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search devices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <span className={styles.searchStats}>
            {filteredDevices.length} devices found
          </span>
        </div>
      </div>

      {/* Devices Table */}
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <div className={styles.loadingText}>Loading devices...</div>
        </div>
      ) : filteredDevices.length === 0 ? (
        <div className={styles.emptyState}>
          <DevicesIcon className={styles.emptyStateIcon} />
          <h3 className={styles.emptyStateTitle}>No devices found</h3>
          <p className={styles.emptyStateText}>
            {search ? 'No devices match your search criteria.' : 'This user has no devices assigned.'}
          </p>
        </div>
      ) : (
        <div className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <h3 className={styles.tableTitle}>User Devices</h3>
            <div className={styles.tableBadge}>{filteredDevices.length} devices</div>
          </div>
          <table className={styles.customTable}>
            <thead>
              <tr>
                <th>Device Details</th>
                <th>Customer & SIM</th>
                <th>Battery & Power</th>
                <th>Speed & Distance</th>
                <th>Last Update</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDevices.map((device) => (
                <tr key={device.id}>
                  <td>
                    <div className={styles.deviceCard}>
                      <div className={styles.deviceAvatar}>
                        <DevicesIcon />
                      </div>
                      <div className={styles.deviceInfo}>
                        <h4>{device.name}</h4>
                        <p>{device.unique_id}</p>
                        <span className={styles.deviceModel}>{device.model}</span>
                        <div className={styles.deviceCategory}>
                          <Chip
                            label={device.category}
                            size="small"
                            sx={{
                              bgcolor: alpha(getPrimaryColor(), 0.1),
                              color: getPrimaryColor(),
                              fontSize: '10px',
                              mt: 0.5
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.customerInfo}>
                      <div className={styles.customerName}>
                        <strong>{device.attributes.customer_name || 'N/A'}</strong>
                      </div>
                      <div className={styles.simInfo}>
                        <span className={styles.simLabel}>SIM:</span>
                        <span className={styles.simNumber}>{device.sim_number || 'N/A'}</span>
                      </div>
                      <div className={styles.simInfo}>
                        <span className={styles.simLabel}>MDN:</span>
                        <span className={styles.simNumber}>{device.attributes.mdn_number || 'N/A'}</span>
                      </div>
                      <div className={styles.phoneInfo}>
                        <span className={styles.phoneLabel}>Phone:</span>
                        <span className={styles.phoneNumber}>{device.phone || 'N/A'}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.batteryInfo}>
                      <BatteryIcon
                        className={styles.batteryIcon}
                        style={{ color: getBatteryColor(device.attributes.battery_level) }}
                      />
                      <div className={styles.batteryDetails}>
                        <span>{device.attributes.battery_level}%</span>
                        <div className={styles.batteryStatus}>
                          <Chip
                            label={device.attributes.battery}
                            size="small"
                            sx={{
                              bgcolor: device.attributes.battery === 'full' ? '#dcfce7' :
                                device.attributes.battery === 'half33' ? '#fef3c7' : '#fee2e2',
                              color: device.attributes.battery === 'full' ? '#166534' :
                                device.attributes.battery === 'half33' ? '#92400e' : '#991b1b',
                              fontSize: '10px'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.speedInfo}>
                      <SpeedIcon className={styles.speedIcon} />
                      <div className={styles.speedDetails}>
                        <span>{device.attributes.latest_speed || 0} km/h</span>
                        <div className={styles.distanceInfo}>
                          <span className={styles.totalDistance}>
                            Total: {device.attributes.total_distance.toFixed(1)} km
                          </span>
                          <span className={styles.todayDistance}>
                            Today: {device.attributes.today_distance.toFixed(1)} km
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.timeInfo}>
                      <AccessTimeIcon className={styles.timeIcon} />
                      <div className={styles.timeDetails}>
                        <span>{formatDate(device.attributes.last_updated_at || 0)}</span>
                        {device.attributes.stopped_at && (
                          <div className={styles.stoppedInfo}>
                            <span className={styles.stoppedLabel}>Stopped:</span>
                            <span className={styles.stoppedTime}>
                              {new Date(device.attributes.stopped_at).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className={styles.tableActions}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/vehicle-tracking/${device.id}`)}
                          sx={{
                            bgcolor: alpha(getPrimaryColor(), 0.1),
                            color: getPrimaryColor(),
                            '&:hover': {
                              bgcolor: alpha(getPrimaryColor(), 0.2),
                            },
                          }}
                        >
                          <LocationOnIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredDevices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ borderTop: '1px solid #e2e8f0' }}
          />
        </div>
      )}
    </div>
  );
};

export default UserDevices;
